// Assuming you have these imports
import { auth } from "../../config/main_config";
import { getValidToken } from "./getValidToken.util";

interface FetchOptions extends RequestInit {
    headers?: HeadersInit;
    body?: BodyInit | null;
    companyID?: string;
}

interface APIResponse<T = any> {
    data: T;
    status: number;
    statusText: string;
}

export const deiterateAPI = {
    request: async function <T = any>(
        method: string,
        url: string,
        options: FetchOptions = {},
        requireAuth: boolean = true,
        apiURL: string = (window as any).apiURL ? (window as any).apiURL : (import.meta.env.VITE_apiURL as string)
    ): Promise<T> {
        const baseURL = apiURL;

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            'company-id': options?.companyID || (window as any).companyID ? (window as any).companyID : localStorage.getItem('companyID') || '',
            ...(options.headers || {}),
            'customer-id': options?.companyID || (window as any).companyID ? (window as any).companyID : localStorage.getItem('companyID') || '',
            ...(options.headers || {}),
        };

        // Only try to get token if there's a current user
        // If requireAuth is true but no user, the request will fail on the backend
        if (auth.currentUser) {
            try {
                const idToken = await getValidToken(auth);
                if (idToken) {
                    let token = idToken.split('--__--')[0];
                    headers['Authorization'] = `Bearer ${token}`;
                }
            } catch (error) {
                console.error('Error getting ID token:', error);
                // If auth is required and we can't get a token, throw
                if (requireAuth) {
                    throw new Error('Authentication required but failed to get token');
                }
            }
        } else if (requireAuth) {
            console.warn('No current user but auth required. Request may fail on backend.');
        }

        // console.log('Headers for API request:', headers);

        const fetchOptions: RequestInit = {
            method,
            headers,
            // mode: 'no-cors',
            ...options,
        };

        try {
            const response = await fetch(baseURL + url.replaceAll('/deiterateInternalAPI', ''), fetchOptions);
            const data: T = await response.json();

            if (!response.ok) {
                throw new Error(
                    `HTTP error! Status: ${response.status}, Message: ${response.statusText || data}`
                );
            }

            return data;
        } catch (error) {
            console.error('API request error:', error);
            throw error;
        }
    },

    POST<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('POST', url, options, ...args);
    },

    GET<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('GET', url, options, ...args);
    },

    PUT<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('PUT', url, options, ...args);
    },

    DELETE<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('DELETE', url, options, ...args);
    },

    PATCH<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('PATCH', url, options, ...args);
    },

    UPDATE<T = any>(url: string, options: FetchOptions = {}, ...args: any[]): Promise<T> {
        return deiterateAPI.request<T>('UPDATE', url, options, ...args);
    },

    // Additional methods can be added here if needed
};
